import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import React from 'react';
import { Proficiency as LibProficiency } from 'auto-converted/lib/Proficiency';
import { SectionsHash as LibSectionsHash } from 'auto-converted/lib/SectionsHash';
import type { LocString } from 'schema/schema';
import { http, queryClient } from 'util/fetch';

interface SkillProficiencyLabels {
  name?: string;
  description?: string;
}

// this list is not exhaustive, but it's a good start
export type Settings = {
  allow_external_proposal_sharing?: boolean;
  allow_proposal_specific_api_keys?: boolean;
  enable_skill_proficiency?: boolean;
  international_enabled?: boolean;
  require_upn?: boolean;
  sensitive_fields_enabled?: boolean;
  show_add_skill_outside_project_experience?: boolean;
  show_certifications?: boolean;
  show_courses?: boolean;
  show_key_qualifications_key_points?: boolean;
  show_project_experiences_area?: boolean;
  show_project_experiences_customer_contacts?: boolean;
  show_project_experiences_customer_description?: boolean;
  show_project_experiences_customer_value_proposition?: boolean;
  show_project_experiences_extent_hours?: boolean;
  show_project_experiences_location_country_code?: boolean;
  show_project_experiences_percent_allocated?: boolean;
  show_project_experiences_project_extent?: boolean;
  show_project_experiences_project_extent_hours?: boolean;
  show_project_experiences_project_type?: boolean;
  show_project_experiences_related_work_experience_id?: boolean;
  show_project_experiences_roles_summary?: boolean;
  show_project_experiences_total_extent?: boolean;
  show_project_experiences_total_extent_hours?: boolean;
  show_project_experiences_location_delivery?: boolean;
  show_project_experiences_contract_type?: boolean;
  show_references_tab?: boolean;
  limit_reference_draft_creation?: boolean;

  educations?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  certifications?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  courses?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  project_experiences?: {
    // No interface settings for disable_ui_edits_for_section_types
  };
  blogs?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  positions?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  mentorings?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  honors_awards?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  presentations?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  recommendations?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  work_experiences?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  personal_details?: {
    // No interface settings for disable_ui_edits_for_section_types
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  key_qualifications?: {
    // No interface settings for disable_ui_edits_for_section_types
  };

  // string or number?
  project_experiences_long_description_recommended_length?: number;
  // string or number?
  project_experiences_roles_long_description_recommended_length?: number;

  skill_proficiency_labels?: Record<
    string,
    | {
        1?: SkillProficiencyLabels;
        2?: SkillProficiencyLabels;
        3?: SkillProficiencyLabels;
        4?: SkillProficiencyLabels;
        5?: SkillProficiencyLabels;
      }
    | undefined
  >;
  skill_proficiency_levels?: 3 | 5;

  external_unique_id_help_texts?: LocString;
  upn_help_texts?: LocString;

  show_blogs?: boolean;
  show_positions?: boolean;
  show_mentorings?: boolean;
  show_honors_awards?: boolean;
  show_presentations?: boolean;
  show_recommendations?: boolean;
  show_work_experiences?: boolean;
  show_reference_project_area?: boolean;
  show_reference_project_type?: boolean;
  show_reference_project_contract_type?: boolean;
  show_personal_details_twitter?: boolean;
  show_reference_project_extent?: boolean;
  show_personal_details_landline?: boolean;
  show_personal_details_born_year?: boolean;
  show_personal_details_external_unique_id?: boolean;
  show_personal_details_place_of_residence?: boolean;
  show_key_qualifications_tag_line?: boolean;
  show_reference_project_architect?: boolean;
  show_personal_details_nationality?: boolean;
  show_reference_project_extent_old?: boolean;
  show_reference_project_extent_hours?: boolean;
  show_reference_project_introduction?: boolean;
  show_reference_project_total_extent?: boolean;
  show_personal_details_name_multilang?: boolean;
  show_reference_project_building_owner?: boolean;
  show_reference_project_extent_hours_old?: boolean;
  show_reference_project_total_extent_hours?: boolean;
  show_reference_project_location_country_code?: boolean;
  show_reference_project_collaborating_partners?: boolean;
  key_qualifications_tag_line_recommended_length?: string;
  show_key_qualifications_key_points_long_description?: boolean;
  // string or number?
  key_qualifications_long_description_recommended_length?: string;
};

export type SettingsKeys = keyof Settings;

interface LastModified {
  id: string;
  name: string | null;
  updated_at: string;
}

type LastModifiedSettings = {
  [key in SettingsKeys]: LastModified;
};

const queries = createQueryKeys('interface-settings', {
  settings: {
    queryKey: null,
    queryFn: ({ signal }) =>
      // the only remaining interface setting exposed externally
      http.get<Settings>('/api/v1/interface_setting', { signal }),
  },
  lastModified: {
    queryKey: null,
    queryFn: ({ signal }) =>
      http.get<LastModifiedSettings>('/interface_setting/last_modified', {
        signal,
      }),
  },
});

const InterfaceSettingsContext = React.createContext<
  Settings | undefined | null
>(null);

export const InterfaceSettingsProvider: React.VFC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data } = useQuery({ ...queries.settings, staleTime: 60_000 });

  return (
    <InterfaceSettingsContext.Provider value={data}>
      {children}
    </InterfaceSettingsContext.Provider>
  );
};

export function useInterfaceSettings() {
  const settingsContext = React.useContext(InterfaceSettingsContext);

  // If `context` is `null` (not `undefined`, as that means "loading"), we
  // fetch the data here. This is to guard against missing ContextProvider.
  // This avoids setting up hundreds of extra `QueryObserver`s.
  const [{ data }] = useQueries({
    queries:
      settingsContext === null
        ? [{ ...queries.settings, staleTime: 60_000 }]
        : [{ enabled: false }],
  });

  return settingsContext ?? data;
}

export function useInterfaceSetting<K extends SettingsKeys>(
  key: K,
): Settings[K] | undefined {
  const data = useInterfaceSettings();

  if (!data) {
    return;
  }

  return data[key] ?? undefined;
}

function useBaseSectionsHash(preview: boolean) {
  const data = useInterfaceSettings();

  return React.useMemo(() => {
    if (!data) {
      return new LibSectionsHash({}, preview);
    }

    return new LibSectionsHash(data, preview);
  }, [data, preview]);
}

export function useSectionsHash() {
  return useBaseSectionsHash(false);
}

export function useSectionsHashPreview() {
  return useBaseSectionsHash(true);
}

export function useProficiency() {
  const data = useInterfaceSettings();

  return React.useMemo(() => {
    if (!data) {
      return new LibProficiency({});
    }

    return new LibProficiency({
      enable_skill_proficiency: data.enable_skill_proficiency,
      skill_proficiency_levels: data.skill_proficiency_levels,
      skill_proficiency_labels: data.skill_proficiency_labels,
    });
  }, [data]);
}

export function useInterfaceSettingLastModified<K extends SettingsKeys>(
  key: K,
): LastModifiedSettings[K] | undefined {
  const { data } = useQuery({ ...queries.lastModified, staleTime: 60_000 });

  if (!data) {
    return;
  }

  return data[key] ?? undefined;
}

export function useUpdateInterfaceSetting() {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationKey: ['interface-setting-update'],
    mutationFn: (interface_setting: Settings) =>
      http.put<Settings>('/interface_setting', { body: { interface_setting } }),
    onSuccess: (data) =>
      queryClient.setQueryData(queries.settings.queryKey, data),
    onError: () => queryClient.invalidateQueries(queries.settings),
    onSettled: () => queryClient.invalidateQueries(queries.lastModified),
  });

  return mutate;
}

export async function prefetchInterfaceSettings() {
  await queryClient.prefetchQuery({ ...queries.settings, staleTime: Infinity });
}

export async function prefetchInterfaceSettingsLastModified() {
  await queryClient.prefetchQuery({
    ...queries.lastModified,
    staleTime: Infinity,
  });
}
